/** @jsx jsx */

import { jsx } from '@emotion/core';
import { ComponentProps, ReactNode } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import { Spacer, Surface, Text } from '@zapier/design-system';
import { singular } from '@zapier/common-utils';

import ExtendedIcon from 'app/developer-v3/components/ExtendedIcon';
import LinkWithArrow from 'app/developer-v3/components/LinkWithArrow';
import { useZapUrl, useToggle } from 'app/developer-v3/hooks';

import Styles from './Dropdown.styles';
import Title from '../CTA/Title';
import Item from './Item';
import Ordinal from '../Ordinal';

import type { ZapTaskDetailType } from 'app/developer-v3/pages/versionOverview/types';

type Props = {
  action: ReactNode;
  description: string;
  icon: ComponentProps<typeof ExtendedIcon>['name'];
  isActive: boolean;
  ordinal: number;
  title: string;
  type: 'triggers' | 'actions';
  zapTaskDetails: Array<ZapTaskDetailType>;
};

export default function Dropdown({
  action,
  description,
  icon,
  isActive,
  ordinal,
  title,
  type,
  zapTaskDetails,
}: Props) {
  const [isOpen, toggleOpen] = useToggle();
  const createZapUrl = useZapUrl();

  return (
    <Surface borderColor="GrayWarm4">
      {/*
      // @ts-ignore -todo update TS version to allow for onToggle as a prop*/}
      <details onToggle={toggleOpen}>
        <summary css={Styles.summary}>
          <Ordinal isActive={isActive} number={ordinal} />
          <Title icon={icon}>{title}</Title>
          <div css={Styles.arrow}>
            <Icon name={isOpen ? 'arrowSmallUp' : 'arrowSmallDown'} size={30} />
          </div>
          <span />
          <Text>{description}</Text>
        </summary>
        {zapTaskDetails.map((zapTaskDetail, i) => {
          const zapUrl = createZapUrl({
            action: zapTaskDetail.key,
            actionLabel: zapTaskDetail.name,
            platformSchemaType: zapTaskDetail.type,
          });

          return (
            <aside
              aria-label={`${zapTaskDetail.name} details`}
              css={Styles.details}
              key={`${zapTaskDetail.name}_${i}`}
            >
              <ul css={Styles.todos}>
                <Text>{zapTaskDetail.name}</Text>
                <Spacer height={5} />
                <li
                  aria-label={`zap ${
                    zapTaskDetail.hasZap ? 'found' : 'required'
                  }`}
                  css={Styles.todo}
                >
                  <Item
                    isSuccess={zapTaskDetail.hasZap}
                    text={`Create Zap with this ${singular(type)}`}
                  />
                </li>
                <Spacer height={5} />
                <li
                  aria-label={`task ${
                    zapTaskDetail.hasTask ? 'found' : 'required'
                  }`}
                  css={Styles.todo}
                >
                  <Item isSuccess={zapTaskDetail.hasTask} text="Test Zap" />
                </li>
              </ul>
              <LinkWithArrow
                ariaLabel="Create a Zap"
                href={zapUrl}
                iconName="arrowOffsite"
              >
                Create a Zap
              </LinkWithArrow>
            </aside>
          );
        })}
        <footer css={Styles.footer}>{action}</footer>
      </details>
    </Surface>
  );
}
